import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf } from "../components/Decor";
import { api, formatApiErrorDetail } from "../lib/api";
import { LOGO_URL } from "../lib/site";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/login", form);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-nightgreen flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gold/10" aria-hidden="true" />
      <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-forest/60" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-sm bg-cream rounded-3xl shadow-2xl p-8"
      >
        <div className="flex flex-col items-center text-center">
          <img src={LOGO_URL} alt="Unique Organic Farm logo" className="w-16 h-16 rounded-full ring-1 ring-gold/40 object-cover shadow" />
          <h1 className="mt-4 font-serif text-2xl font-semibold text-forest">Farm Admin</h1>
          <p className="mt-1 text-xs text-muted inline-flex items-center gap-1.5">
            <Leaf className="w-3 h-3 text-gold" />
            Unique Organic Farm dashboard
          </p>
        </div>
        <form data-testid="admin-login-form" onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label htmlFor="admin-email" className="block text-xs font-semibold uppercase tracking-[0.14em] text-forest/70 mb-1.5">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              autoComplete="username"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-xl border border-sand bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              placeholder="admin@uniqueorganicfarm.com"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs font-semibold uppercase tracking-[0.14em] text-forest/70 mb-1.5">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full rounded-xl border border-sand bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-forest px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-all hover:bg-nightgreen hover:shadow-lg disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <a href="/" className="mt-5 block text-center text-xs text-muted hover:text-forest transition-colors">
          ← Back to the website
        </a>
      </motion.div>
    </div>
  );
}
